<template>
  <div class="tool-page qzm-page">
    <div class="tool-content">
      <div class="tool-heading">
        <div>
          <h2>视频去字幕</h2>
          <p>上传视频文件或链接，选择模型与检测方式，自动擦除硬字幕与水印</p>
        </div>
        <app-icon class="app-icon" name="text_fields"></app-icon>
      </div>

      <div class="tool-card">
        <!-- 选择视频 -->
        <section class="section-block">
          <div class="section-header">
            <span class="section-title">选择视频</span>
            <el-button type="text" size="small" class="record-button" @click="$router.push('/user2/record')">
              <i class="el-icon-time"></i> 制作记录
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
          <qhyuploadv2 ref="upload" :limit="1"></qhyuploadv2>
        </section>

        <!-- 模型（与检测方式联动：免费模型只支持框选） -->
        <section class="section-block" v-if="qzmModes.length">
          <div class="section-title">去字幕模型</div>
          <div v-for="mode in qzmModes" :key="mode.code" class="qzm-mode"
            :class="{ active: qzmMode === mode.code }" @click="selectQzmMode(mode.code)">
            <div class="qzm-mode-main">
              <div class="qzm-mode-name">
                {{ mode.name }}
                <span class="qzm-mode-dur" v-if="mode.max_duration_seconds">
                  ≤ {{ Math.floor(mode.max_duration_seconds / 60) }} 分钟
                </span>
              </div>
              <div class="qzm-mode-desc">{{ mode.description }}</div>
            </div>
            <div class="qzm-mode-side">
              <span class="qzm-mode-fee">{{ mode.fee || '免费' }}</span>
              <span class="qzm-radio"></span>
            </div>
          </div>
        </section>

        <!-- 检测方式 -->
        <section class="section-block">
          <div class="section-title">检测方式</div>
          <div class="chip-row">
            <button v-for="opt in detectionOptions" :key="opt.value" type="button" class="chip qzm-chip"
              :class="{ active: qzmDetection === opt.value }" @click="selectDetection(opt.value)">
              {{ opt.label }}
              <span v-if="opt.value === 'region'" class="qzm-chip-badge">更精准</span>
            </button>
          </div>

          <div v-if="qzmDetection === 'region'" class="qzm-region-row" @click="openRegionDialog">
            <span class="qzm-region-state">
              <i class="qzm-dot" :class="{ pending: !qzmBoxes.length }"></i>
              {{ qzmBoxes.length ? '已设置框选位置' : '还没有框选位置' }}
            </span>
            <span class="qzm-region-action">{{ qzmBoxes.length ? '重新框选' : '去框选' }}</span>
          </div>
          <p class="mode-tip" v-else>{{ fullTip }}</p>
        </section>

        <el-button class="submit-btn" type="primary" :loading="qzmSubmitting" @click="submitQzm">开始制作</el-button>
        <p class="qzm-fee-tip" v-if="currentMode">
          当前：{{ currentMode.name }} · {{ currentMode.fee || '免费' }}
          <span v-if="qzmDetection === 'region'"> · 框选位置{{ qzmBoxes.length ? '已设置' : '未设置' }}</span>
        </p>
      </div>
    </div>

    <!-- 框选字幕位置 -->
    <el-dialog title="框选字幕位置" :visible.sync="regionVisible" :width="isMobile ? '94%' : '720px'"
      :close-on-click-modal="false" @opened="onRegionOpened">
      <p class="qzm-region-tip">在画面上拖出字幕所在区域（只支持一个框），坐标会按视频原始分辨率提交；单击画面可播放/暂停，下方进度条可拖动。</p>
      <div ref="stage" class="qzm-stage">
        <div ref="frame" class="qzm-frame" @pointerdown="onStageDown">
          <!-- 画面上不能放原生 controls：它会盖住画面并抢走拖拽事件 -->
          <video ref="regionVideo" class="qzm-video" :src="regionVideoUrl" playsinline
            @click="togglePlay" @play="playing = true" @pause="playing = false"
            @timeupdate="onTimeUpdate" @loadedmetadata="onVideoMeta"></video>
          <div v-if="displayBox" class="qzm-box" :style="boxStyle">
            <span class="qzm-handle qzm-handle-nw" data-h="nw"></span>
            <span class="qzm-handle qzm-handle-ne" data-h="ne"></span>
            <span class="qzm-handle qzm-handle-sw" data-h="sw"></span>
            <span class="qzm-handle qzm-handle-se" data-h="se"></span>
          </div>
        </div>
      </div>

      <!-- 播放控制放在画面外，避免与框选区域重叠 -->
      <div class="qzm-ctrl">
        <button type="button" class="qzm-ctrl-btn" @click="togglePlay">
          <i :class="playing ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
        </button>
        <span class="qzm-ctrl-time">{{ fmtTime(currentTime) }} / {{ fmtTime(duration) }}</span>
        <div ref="bar" class="qzm-ctrl-bar" @click="seekByEvent">
          <div class="qzm-ctrl-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>

      <p class="qzm-region-meta">
        <span v-if="qzmVideoSize.width">视频分辨率：{{ qzmVideoSize.width }} × {{ qzmVideoSize.height }}</span>
        <span v-else>正在读取视频尺寸…</span>
        <span v-if="qzmBoxes.length"> · 已保存选区：{{ qzmBoxes[0].w }} × {{ qzmBoxes[0].h }}</span>
      </p>
      <div class="ku-dialog-footer">
        <el-button size="small" @click="clearRegion">清除框选</el-button>
        <el-button size="small" type="primary" @click="saveRegion">保存框选</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import qhyuploadv2 from '@/components/common/qhyuploadv2.vue'

export default {
  name: 'User2Qzm',
  components: { qhyuploadv2 },
  data() {
    return {
      qzmModes: [],
      qzmMode: '',
      qzmDetection: 'full',
      qzmBoxes: [],
      qzmVideoSize: { width: 0, height: 0 },
      qzmSubmitting: false,
      isMobile: false,
      // 框选
      regionVisible: false,
      regionVideoUrl: '',
      displayBox: null,
      drag: null,
      // 播放（控制条放在画面外，画面上不能有原生 controls，否则会抢走框选拖拽）
      playing: false,
      currentTime: 0,
      duration: 0,
      justDragged: false,
      parsedUrl: '',
      parsedFrom: ''
    }
  },
  computed: {
    currentMode() {
      const code = this.qzmMode
      for (let i = 0; i < this.qzmModes.length; i++) {
        if (this.qzmModes[i].code === code) return this.qzmModes[i]
      }
      return null
    },
    // 检测方式由所选模型决定（免费模型只支持框选）
    detectionOptions() {
      const all = [
        { value: 'full', label: '全屏' },
        { value: 'region', label: '框选位置' }
      ]
      const allowed = (this.currentMode && this.currentMode.detection_modes) || ['full', 'region']
      return all.filter(opt => allowed.indexOf(opt.value) > -1)
    },
    fullTip() {
      return this.currentMode && this.currentMode.detection_modes && this.currentMode.detection_modes.indexOf('full') > -1
        ? '全屏检测会扫描整个画面，适合字幕位置固定的视频。'
        : ''
    },
    boxStyle() {
      const b = this.displayBox || { x: 0, y: 0, w: 0, h: 0 }
      return { left: b.x + 'px', top: b.y + 'px', width: b.w + 'px', height: b.h + 'px' }
    },
    progressPercent() {
      if (!this.duration) return 0
      return Math.min(100, Math.max(0, (this.currentTime / this.duration) * 100))
    }
  },
  methods: {
    // ── 模型 ──
    loadQzmModes() {
      this.$ajax.get('api/v1/users/qzm/modes').then(res => {
        const body = res.data || {}
        const data = body.data || {}
        const modes = data.modes || []
        if (!modes.length) return
        this.qzmModes = modes
        const cached = window.localStorage.getItem('qzmSelectedMode')
        let code = cached || data.default_mode || ''
        let matched = false
        for (let i = 0; i < modes.length; i++) {
          if (modes[i].code === code) matched = true
        }
        if (!matched) code = modes[0].code
        this.applyMode(code)
      }).catch(() => {
        this.$msg.error('去字幕模型加载失败')
      })
    },
    selectQzmMode(code) {
      if (code === this.qzmMode && this.qzmDetection) return
      this.applyMode(code)
      window.localStorage.setItem('qzmSelectedMode', code)
    },
    applyMode(code) {
      this.qzmMode = code
      const mode = this.currentMode
      const allowed = (mode && mode.detection_modes) || ['full', 'region']
      if (allowed.indexOf(this.qzmDetection) === -1) {
        this.qzmDetection = allowed[0] || 'full'
      }
    },
    // ── 检测方式 ──
    selectDetection(value) {
      if (value === this.qzmDetection) return
      if (value === 'region') {
        if (this.qzmBoxes.length) {
          this.qzmDetection = 'region'
          return
        }
        this.openRegionDialog()
        return
      }
      this.qzmDetection = value
    },
    // ── 框选 ──
    openRegionDialog() {
      const items = this.uploadItems()
      if (!items.length) {
        this.$msg.error('请先上传视频文件或链接')
        return
      }
      const item = items[0]
      if (item.uid) {
        this.regionVideoUrl = item.url
        this.regionVisible = true
        return
      }
      if (this.parsedUrl && this.parsedFrom === item.url) {
        this.regionVideoUrl = this.parsedUrl
        this.regionVisible = true
        return
      }
      const loading = this.$loading({ lock: true, text: '解析视频链接…', background: 'rgba(255,255,255,.8)' })
      this.$ajax.post('qhy/user/qsyjk', { url: item.url }, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }).then(res => {
        const body = res.data || {}
        const data = body.data || {}
        const playUrl = data.playAddr || data.url || data.play_url || ''
        if (body.status === 200 && playUrl) {
          this.parsedUrl = playUrl
          this.parsedFrom = item.url
          this.regionVideoUrl = playUrl
          this.regionVisible = true
        } else {
          this.$msg.error(body.msg || '链接解析失败，可改为上传视频文件')
        }
      }).catch(() => {
        this.$msg.error('链接解析失败，可改为上传视频文件')
      }).finally(() => {
        loading.close()
      })
    },
    onRegionOpened() {
      this.displayBox = null
      this.qzmVideoSize = { width: 0, height: 0 }
      this.playing = false
      this.currentTime = 0
      this.duration = 0
    },
    // ── 播放控制（都在画面外）──
    togglePlay() {
      if (this.justDragged) {
        this.justDragged = false
        return
      }
      const video = this.$refs.regionVideo
      if (!video) return
      if (video.paused) {
        const played = video.play()
        if (played && played.catch) played.catch(() => { this.$msg.error('视频无法播放，请检查链接是否可用') })
      } else {
        video.pause()
      }
    },
    onTimeUpdate() {
      const video = this.$refs.regionVideo
      if (!video) return
      this.currentTime = video.currentTime || 0
      this.duration = video.duration || 0
    },
    seekByEvent(e) {
      const bar = this.$refs.bar
      const video = this.$refs.regionVideo
      if (!bar || !video || !video.duration) return
      const rect = bar.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
      video.currentTime = ratio * video.duration
      this.currentTime = video.currentTime
    },
    fmtTime(seconds) {
      const total = Math.max(0, Math.floor(Number(seconds) || 0))
      const m = Math.floor(total / 60)
      const s = total % 60
      return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s)
    },
    onVideoMeta() {
      const video = this.$refs.regionVideo
      if (!video) return
      this.qzmVideoSize = { width: video.videoWidth || 0, height: video.videoHeight || 0 }
    },
    frameRect() {
      const frame = this.$refs.frame
      return frame ? frame.getBoundingClientRect() : null
    },
    clamp(value, min, max) {
      return Math.max(min, Math.min(max, value))
    },
    inBox(point, box) {
      return point.x >= box.x && point.x <= box.x + box.w && point.y >= box.y && point.y <= box.y + box.h
    },
    onStageDown(e) {
      const rect = this.frameRect()
      if (!rect || !rect.width) return
      const point = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      const handle = e.target && e.target.getAttribute ? e.target.getAttribute('data-h') : ''
      let mode = 'create'
      if (handle) {
        mode = 'resize'
      } else if (this.displayBox && this.inBox(point, this.displayBox)) {
        mode = 'move'
      }
      this.drag = {
        mode: mode,
        handle: handle || '',
        start: point,
        origin: this.displayBox ? Object.assign({}, this.displayBox) : null
      }
      if (mode === 'create') {
        this.displayBox = { x: point.x, y: point.y, w: 0, h: 0 }
      }
      if (e.target && e.target.setPointerCapture && e.pointerId != null) {
        try { e.target.setPointerCapture(e.pointerId) } catch (err) { /* ignore */ }
      }
      e.preventDefault()
    },
    onStageMove(e) {
      if (!this.drag) return
      const rect = this.frameRect()
      if (!rect) return
      const point = {
        x: this.clamp(e.clientX - rect.left, 0, rect.width),
        y: this.clamp(e.clientY - rect.top, 0, rect.height)
      }
      const drag = this.drag
      if (drag.mode === 'create') {
        const x = Math.min(drag.start.x, point.x)
        const y = Math.min(drag.start.y, point.y)
        this.displayBox = {
          x: x,
          y: y,
          w: Math.abs(point.x - drag.start.x),
          h: Math.abs(point.y - drag.start.y)
        }
        return
      }
      const origin = drag.origin
      if (!origin) return
      if (drag.mode === 'move') {
        this.displayBox = {
          x: this.clamp(origin.x + point.x - drag.start.x, 0, Math.max(0, rect.width - origin.w)),
          y: this.clamp(origin.y + point.y - drag.start.y, 0, Math.max(0, rect.height - origin.h)),
          w: origin.w,
          h: origin.h
        }
        return
      }
      // resize：按拖动的角落调整
      let left = origin.x
      let top = origin.y
      let right = origin.x + origin.w
      let bottom = origin.y + origin.h
      if (drag.handle.indexOf('w') > -1) left = this.clamp(point.x, 0, right - 8)
      if (drag.handle.indexOf('e') > -1) right = this.clamp(point.x, left + 8, rect.width)
      if (drag.handle.indexOf('n') > -1) top = this.clamp(point.y, 0, bottom - 8)
      if (drag.handle.indexOf('s') > -1) bottom = this.clamp(point.y, top + 8, rect.height)
      this.displayBox = { x: left, y: top, w: right - left, h: bottom - top }
    },
    onStageUp(e) {
      if (!this.drag) return
      const drag = this.drag
      this.drag = null
      if (e && e.target && e.target.releasePointerCapture && e.pointerId != null) {
        try { e.target.releasePointerCapture(e.pointerId) } catch (err) { /* ignore */ }
      }
      let meaningful = false
      if (drag.mode === 'create') {
        const tiny = !this.displayBox || this.displayBox.w < 8 || this.displayBox.h < 8
        if (tiny) this.displayBox = null
        else meaningful = true
      } else {
        meaningful = true
      }
      // 真的拖过框才抑制紧随其后的 click（单击画面仍然可以播放/暂停）
      this.justDragged = meaningful
      setTimeout(() => { this.justDragged = false }, 0)
    },
    clearRegion() {
      this.displayBox = null
      this.qzmBoxes = []
    },
    // 显示坐标 → 视频原始像素（后端按 box.x / video_width 归一化）
    saveRegion() {
      const rect = this.frameRect()
      const size = this.qzmVideoSize
      if (!this.displayBox || !rect || !rect.width) {
        this.$msg.error('请先在画面上框出字幕区域')
        return
      }
      if (!size.width || !size.height) {
        this.$msg.error('视频尺寸还没读出来，请稍等一下')
        return
      }
      const sx = size.width / rect.width
      const sy = size.height / rect.height
      this.qzmBoxes = [{
        x: Math.round(this.displayBox.x * sx),
        y: Math.round(this.displayBox.y * sy),
        w: Math.round(this.displayBox.w * sx),
        h: Math.round(this.displayBox.h * sy),
        startTime: 0,
        endTime: 0
      }]
      this.qzmDetection = 'region'
      this.regionVisible = false
      this.$msg.success('框选位置已保存')
    },
    // ── 提交 ──
    uploadItems() {
      const upload = this.$refs.upload
      if (!upload || !upload.get_urls) return []
      const items = upload.get_urls(1)
      return Array.isArray(items) ? items : []
    },
    submitQzm() {
      if (this.qzmSubmitting) return
      const upload = this.$refs.upload
      if (!upload || !upload.get_urls) {
        this.$msg.error('上传组件未就绪')
        return
      }
      const raw = upload.get_urls(1)
      if (!Array.isArray(raw) || !raw.length) {
        this.$msg.error((raw && raw.msg) || '请先上传视频文件或链接')
        return
      }
      if (!this.qzmMode) {
        this.$msg.error('去字幕模型还没加载完成，请稍后重试')
        return
      }
      if (this.qzmDetection === 'region' && !this.qzmBoxes.length) {
        this.$msg.error('请先点击「框选位置」完成选区')
        return
      }
      const item = raw[0]
      const isUrl = !item.uid
      const payload = {
        url: item.url,
        mode: this.qzmMode,
        detection: this.qzmDetection,
        boxes: this.qzmDetection === 'region' ? this.qzmBoxes : [],
        urllx: isUrl ? 1 : 2,
        videoWidth: this.qzmVideoSize.width || 0,
        videoHeight: this.qzmVideoSize.height || 0
      }
      this.qzmSubmitting = true
      this.$ajax.post('api/v1/users/qzm/submit', payload).then(res => {
        const body = res.data || {}
        if (body.status === 200) {
          upload.clear_url()
          this.qzmBoxes = []
          this.displayBox = null
          this.qzmDetection = this.detectionOptions.length ? this.detectionOptions[0].value : 'full'
          this.$msg.success(body.msg || '任务提交成功')
          this.$confirm('任务已提交，去制作记录查看进度？', '提交成功', {
            confirmButtonText: '去查看',
            cancelButtonText: '继续制作',
            type: 'success'
          }).then(() => {
            this.$router.push('/user2/record')
          }).catch(() => { })
        } else {
          this.$msg.error(body.msg || '任务提交失败')
        }
      }).catch(() => {
        this.$msg.error('任务提交失败，请重试')
      }).finally(() => {
        this.qzmSubmitting = false
      })
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 900
    }
  },
  created() {
    this.loadQzmModes()
  },
  mounted() {
    this.updateViewport()
    window.addEventListener('resize', this.updateViewport)
    document.addEventListener('pointermove', this.onStageMove)
    document.addEventListener('pointerup', this.onStageUp)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport)
    document.removeEventListener('pointermove', this.onStageMove)
    document.removeEventListener('pointerup', this.onStageUp)
  }
}
</script>

<style scoped>
.tool-page {
  min-height: 100%;
  padding: 24px 24px 48px;
  background: #f5f7fa;
  box-sizing: border-box;
}

.tool-content {
  max-width: 1080px;
  margin: 0 auto;
}

.tool-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.tool-heading h2 {
  margin: 0 0 6px;
  color: #1e293b;
  font-size: 22px;
}

.tool-heading p {
  margin: 0;
  color: #94a3b8;
  font-size: 13px;
}

.tool-heading .app-icon {
  padding: 11px;
  border-radius: 12px;
  background: #e8f1fe;
  color: #1a73e8;
  font-size: 28px;
}

.tool-card {
  padding: 24px 24px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, .04);
}

.section-block + .section-block {
  margin-top: 26px;
}

.section-title {
  margin-bottom: 12px;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-header .section-title {
  margin-bottom: 0;
}

/* 制作记录入口：原来的纯文字太不显眼，做成带底色的胶囊按钮 */
.section-header .record-button {
  padding: 4px 12px;
  border: 1px solid #dbeafe;
  border-radius: 16px;
  background: #eff6ff;
  color: #1a73e8;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
  transition: all .2s;
}

.section-header .record-button:hover,
.section-header .record-button:focus {
  border-color: #93c5fd;
  background: #dbeafe;
  color: #1a73e8;
}

.section-header .record-button i {
  margin-right: 4px;
  font-size: 13px;
}

.section-header .record-button i.el-icon-arrow-right {
  margin-right: 0;
  margin-left: 2px;
  font-size: 12px;
}

.mode-tip {
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 12px;
}

/* 模型列表 */
.qzm-mode {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
  transition: all .2s;
}

.qzm-mode:hover {
  border-color: #93c5fd;
}

.qzm-mode.active {
  border-color: #1a73e8;
  background: #eff6ff;
}

.qzm-mode-main {
  flex: 1;
  min-width: 0;
}

.qzm-mode-name {
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
}

.qzm-mode-dur {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  background: #e2e8f0;
  color: #64748b;
  font-size: 11px;
  font-weight: 500;
}

.qzm-mode-desc {
  margin-top: 4px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.6;
}

.qzm-mode-side {
  display: flex;
  flex: none;
  align-items: center;
  gap: 10px;
}

.qzm-mode-fee {
  color: #1a73e8;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.qzm-radio {
  width: 16px;
  height: 16px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  box-sizing: border-box;
}

.qzm-mode.active .qzm-radio {
  border: 5px solid #1a73e8;
}

/* 检测方式 chip */
.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  min-width: 76px;
  padding: 8px 15px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  transition: .2s;
}

.chip:hover {
  border-color: #93c5fd;
  color: #1a73e8;
}

.chip.active {
  border-color: #1a73e8;
  background: #1a73e8;
  color: #fff;
}

.qzm-chip {
  position: relative;
}

.qzm-chip-badge {
  position: absolute;
  top: -9px;
  right: -6px;
  padding: 1px 6px;
  border-radius: 8px;
  background: linear-gradient(90deg, #ff7b4d, #ff4f78);
  color: #fff;
  font-size: 10px;
  line-height: 1.5;
  white-space: nowrap;
}

.qzm-region-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 10px 14px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
  font-size: 12px;
  cursor: pointer;
}

.qzm-region-state {
  display: flex;
  align-items: center;
  color: #475569;
}

.qzm-dot {
  width: 7px;
  height: 7px;
  margin-right: 7px;
  border-radius: 50%;
  background: #22c55e;
}

.qzm-dot.pending {
  background: #f59e0b;
}

.qzm-region-action {
  color: #1a73e8;
  font-weight: 600;
}

.submit-btn {
  width: 100%;
  margin-top: 30px;
  border-color: #1a73e8;
  background: #1a73e8;
}

.qzm-fee-tip {
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 12px;
  text-align: center;
}

/* 框选弹窗 */
.qzm-region-tip {
  margin: 0 0 12px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.7;
}

.qzm-stage {
  display: flex;
  justify-content: center;
  padding: 12px;
  border-radius: 10px;
  background: #0f172a;
  overflow: hidden;
}

.qzm-frame {
  position: relative;
  display: inline-block;
  line-height: 0;
  cursor: crosshair;
  touch-action: none;
}

.qzm-video {
  display: block;
  max-width: 100%;
  max-height: 46vh;
}

.qzm-box {
  position: absolute;
  border: 2px solid #1a73e8;
  background: rgba(26, 115, 232, .18);
  box-sizing: border-box;
}

.qzm-handle {
  position: absolute;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #1a73e8;
  box-sizing: border-box;
}

.qzm-handle-nw {
  top: -7px;
  left: -7px;
  cursor: nwse-resize;
}

.qzm-handle-ne {
  top: -7px;
  right: -7px;
  cursor: nesw-resize;
}

.qzm-handle-sw {
  bottom: -7px;
  left: -7px;
  cursor: nesw-resize;
}

.qzm-handle-se {
  bottom: -7px;
  right: -7px;
  cursor: nwse-resize;
}

.qzm-region-meta {
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 12px;
}

/* 播放控制条（在画面外，不遮挡框选） */
.qzm-ctrl {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.qzm-ctrl-btn {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #dbe3ec;
  border-radius: 50%;
  background: #f8fafc;
  color: #1a73e8;
  font-size: 15px;
  cursor: pointer;
}

.qzm-ctrl-btn:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.qzm-ctrl-time {
  flex: none;
  color: #64748b;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.qzm-ctrl-bar {
  position: relative;
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #e2e8f0;
  cursor: pointer;
}

.qzm-ctrl-fill {
  height: 100%;
  border-radius: 3px;
  background: #1a73e8;
}

.ku-dialog-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

@media (max-width: 900px) {
  .tool-page {
    padding: 14px 14px 32px;
  }

  .tool-heading h2 {
    font-size: 19px;
  }

  .tool-heading p {
    display: none;
  }

  .tool-card {
    padding: 16px 16px 28px;
  }

  .qzm-video {
    max-height: 38vh;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/User2/Qzm.vue